import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowsClockwise, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ALL_TARGETS } from "@/lib/repurposeConstants";
import { ResultCard } from "@/components/repurpose/ResultCard";
import { FormatSelector, SubOptions } from "@/components/repurpose/FormatSelector";
import { RepurposeConfig } from "@/components/repurpose/RepurposeConfig";
import { ChainResults } from "@/components/repurpose/ChainResults";

export const RepurposeModal = ({
  isOpen, onClose, originalContent, originalType, originalPlatform,
  audience, availableModels, defaultTones = ["Direct", "Practical"], defaultModel = "",
}) => {
  const { api } = useAuth();
  const [targetIdx, setTargetIdx] = useState(0);
  const [tones, setTones] = useState(defaultTones);
  const [aiModel, setAiModel] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saveToHistory, setSaveToHistory] = useState(true);
  const [slideCount, setSlideCount] = useState(5);
  const [threadCount, setThreadCount] = useState(5);
  const [emailCount, setEmailCount] = useState(3);
  const [activeCategory, setActiveCategory] = useState("Social");
  const [emojiLevel, setEmojiLevel] = useState("none");

  // Chain mode
  const [chainMode, setChainMode] = useState(false);
  const [chainSelected, setChainSelected] = useState([]);
  const [chainResults, setChainResults] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedChainIdx, setCopiedChainIdx] = useState(null);
  const [chainLimit, setChainLimit] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (availableModels.length > 0) {
      const preferred = defaultModel && availableModels.includes(defaultModel) ? defaultModel : availableModels[0];
      setAiModel(preferred);
    }
  }, [availableModels, defaultModel]);

  useEffect(() => {
    if (isOpen) {
      setResult(null); setChainResults([]); setGenerating(false); setChainMode(false); setChainSelected([]);
      api.get("/usage").then((r) => setChainLimit(r.data.chain_limit)).catch(() => {});
    }
  }, [isOpen, api]);

  const selectedTarget = ALL_TARGETS[targetIdx];
  const chainCost = Math.ceil(chainSelected.length / 2);

  const toggleTone = (tone) => setTones((prev) => prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]);

  const toggleChainTarget = (idx) => {
    setChainSelected((prev) => {
      if (prev.includes(idx)) return prev.filter((i) => i !== idx);
      const limit = chainLimit === null ? 10 : chainLimit;
      if (prev.length >= limit) {
        toast.error(`Max ${limit} format(s) per chain${chainLimit !== null ? ". Upgrade to Pro for up to 10." : ""}`);
        return prev;
      }
      return [...prev, idx];
    });
  };

  const handleRepurpose = async () => {
    if (!aiModel) { toast.error("Select an AI model first"); return; }
    if (!originalContent || originalContent.trim().length === 0) { toast.error("No content to repurpose. Please generate content first."); return; }
    setGenerating(true); setResult(null); setCopied(false);
    try {
      const target = ALL_TARGETS[targetIdx];
      const resp = await api.post("/repurpose", {
        original_content: originalContent, original_type: originalType, original_platform: originalPlatform,
        target_type: target.type, target_platform: target.platform, audience: audience || "Business owners",
        tone_variations: tones, ai_model: aiModel, additional_context: additionalContext,
        slide_count: slideCount, thread_count: threadCount, email_count: emailCount,
        save_to_history: saveToHistory, emoji_level: emojiLevel,
      });
      if (resp.data?.error) { toast.error(resp.data.error_message || resp.data.message || "Repurpose failed"); return; }
      setResult(resp.data);
      toast.success(`Repurposed to ${target.type}! +15 XP`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || "";
      if (err.response?.status === 429) toast.error(detail || "Generation limit reached. Upgrade for more.");
      else if (err.response?.status === 503 || err.response?.status === 502) toast.error("AI model temporarily unavailable. Try again or switch models.");
      else toast.error(detail || "Repurpose failed. Please try again.");
    } finally { setGenerating(false); }
  };

  const handleChainRepurpose = async () => {
    if (!aiModel) { toast.error("Select an AI model first"); return; }
    if (!originalContent) { toast.error("No content to repurpose"); return; }
    if (chainSelected.length === 0) { toast.error("Select at least one target format"); return; }
    setGenerating(true); setChainResults([]); setActiveTab(0);
    try {
      const targets = chainSelected.map((idx) => {
        const t = ALL_TARGETS[idx];
        return { target_type: t.type, target_platform: t.platform,
          slide_count: t.hasSlidCount ? slideCount : 5, thread_count: t.hasThreadCount ? threadCount : 5, email_count: t.hasEmailCount ? emailCount : 3 };
      });
      const resp = await api.post("/repurpose/chain", {
        original_content: originalContent, original_type: originalType, original_platform: originalPlatform,
        audience: audience || "Business owners", tone_variations: tones, ai_model: aiModel,
        additional_context: additionalContext, targets, emoji_level: emojiLevel,
      });
      setChainResults(resp.data.results);
      toast.success(`Chain complete! ${resp.data.targets_completed} format(s) generated. Cost: ${resp.data.chain_cost} credit(s).`);
    } catch (err) { toast.error(err.response?.data?.detail || "Chain repurpose failed"); }
    finally { setGenerating(false); }
  };

  const copyText = async (text, idx) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    if (idx !== undefined) { setCopiedChainIdx(idx); setTimeout(() => setCopiedChainIdx(null), 2000); }
    else { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    toast.success("Copied!");
  };

  if (!isOpen) return null;

  const showSlide = chainMode ? chainSelected.some((i) => ALL_TARGETS[i]?.hasSlidCount) : selectedTarget?.hasSlidCount;
  const showThread = chainMode ? chainSelected.some((i) => ALL_TARGETS[i]?.hasThreadCount) : selectedTarget?.hasThreadCount;
  const showEmail = chainMode ? chainSelected.some((i) => ALL_TARGETS[i]?.hasEmailCount) : selectedTarget?.hasEmailCount;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-start justify-center md:overflow-y-auto md:py-8 px-0 md:px-4">
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
        className="w-full md:max-w-2xl bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-t-2xl md:rounded-xl overflow-hidden max-h-[90vh] md:max-h-none flex flex-col"
        data-testid="repurpose-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-[var(--border)]">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
              <ArrowsClockwise size={20} className="text-[#FF4400]" />Repurpose Content
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">Transform into 20+ formats</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="repurpose-modal-close">
            <X size={20} />
          </button>
        </div>

        <div ref={formRef} className="px-4 sm:px-6 space-y-5 py-5 sm:py-6 overflow-y-auto flex-1">
          {/* Mode toggle */}
          <div className="flex items-center gap-3 bg-[var(--bg-base)] rounded-lg p-1">
            <button onClick={() => { setChainMode(false); setChainResults([]); setChainSelected([]); }} data-testid="repurpose-single-mode"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${!chainMode ? "bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm border border-[var(--border)]" : "text-[var(--text-muted)]"}`}>
              <ArrowsClockwise size={16} /> Single
            </button>
            <button onClick={() => { setChainMode(true); setResult(null); }} data-testid="repurpose-chain-mode"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${chainMode ? "bg-[var(--bg-card)] text-[#FF4400] shadow-sm border border-[#FF4400]/30" : "text-[var(--text-muted)]"}`}>
              <LinkSimple size={16} /> Chain
              {chainLimit !== null && <span className="text-[10px] bg-[var(--accent-subtle)] text-[#FF4400] px-1.5 py-0.5 rounded-full">{chainLimit} max</span>}
            </button>
          </div>

          {chainMode && (
            <div className="bg-[var(--accent-subtle)] border border-[#FF4400]/15 rounded-md px-4 py-2.5">
              <p className="text-xs text-[var(--text-secondary)]">
                <span className="font-semibold text-[#FF4400]">Chain Mode</span> — Multi-select formats. 1 credit per 2 formats (50% off).
                {chainSelected.length > 0 && <span className="ml-1 font-medium text-[var(--text-primary)]">Selected: {chainSelected.length} = {chainCost} credit{chainCost !== 1 ? "s" : ""}</span>}
              </p>
            </div>
          )}

          <FormatSelector chainMode={chainMode} activeCategory={activeCategory} setActiveCategory={setActiveCategory}
            targetIdx={targetIdx} setTargetIdx={setTargetIdx} chainSelected={chainSelected} toggleChainTarget={toggleChainTarget} />

          <SubOptions showSlide={showSlide} showThread={showThread} showEmail={showEmail}
            slideCount={slideCount} setSlideCount={setSlideCount} threadCount={threadCount} setThreadCount={setThreadCount}
            emailCount={emailCount} setEmailCount={setEmailCount} />

          <RepurposeConfig tones={tones} toggleTone={toggleTone} emojiLevel={emojiLevel} setEmojiLevel={setEmojiLevel}
            additionalContext={additionalContext} setAdditionalContext={setAdditionalContext}
            aiModel={aiModel} setAiModel={setAiModel} availableModels={availableModels}
            chainMode={chainMode} saveToHistory={saveToHistory} setSaveToHistory={setSaveToHistory} />

          {/* Generate button */}
          {!chainMode ? (
            <button onClick={handleRepurpose} disabled={generating || !aiModel}
              className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-12 rounded-md text-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.3)] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="repurpose-submit-button">
              {generating ? <><ArrowsClockwise size={18} className="animate-spin" /> Repurposing...</> : <><ArrowsClockwise size={18} /> Repurpose to {selectedTarget?.type || "..."}</>}
            </button>
          ) : (
            <button onClick={handleChainRepurpose} disabled={generating || !aiModel || chainSelected.length === 0}
              className="w-full bg-gradient-to-r from-[#FF4400] to-[#FF6633] hover:from-[#FF5500] hover:to-[#FF7744] text-white font-semibold h-12 rounded-md text-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.3)] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="chain-submit-button">
              {generating ? <><ArrowsClockwise size={18} className="animate-spin" /> Generating...</> : <><LinkSimple size={18} /> Chain ({chainSelected.length}) — {chainCost} credit{chainCost !== 1 ? "s" : ""}</>}
            </button>
          )}

          {/* Single mode result */}
          <AnimatePresence>
            {!chainMode && result && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="repurpose-result">
                <div className="h-px bg-[var(--bg-hover)]" />
                <ResultCard result={result} copied={copied} onCopy={() => copyText(result.repurposed_content)} savedIndicator={saveToHistory} />
                <button onClick={() => { setResult(null); setCopied(false); formRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="w-full py-2.5 rounded-md text-sm font-medium border border-[#FF4400]/30 text-[#FF4400] hover:bg-[#FF4400]/5 transition-colors flex items-center justify-center gap-2"
                  data-testid="repurpose-another-btn">
                  <ArrowsClockwise size={16} /> Repurpose to Another Format
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chain mode results */}
          <AnimatePresence>
            {chainMode && chainResults.length > 0 && (
              <ChainResults chainResults={chainResults} activeTab={activeTab} setActiveTab={setActiveTab}
                copiedChainIdx={copiedChainIdx} onCopyChain={copyText}
                onReset={() => { setChainResults([]); setChainSelected([]); }} />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
